import React, { FC, useState, useEffect, useContext, useRef } from 'react';
import { ProfileSection } from './ProfileSection';
import { sessionStore } from 'src/store/sessionStore';
import { useUpdateTrainerProfile } from 'src/api/mutations/useUpdateTrainerProfile';
import { uploadTrainerFile } from 'src/api/services/firebaseStorage';
import { Modal } from 'antd';
import { FullScreenVideoModal } from 'src/components/Modals/FullScreenVideoModal/FullScreenVideoModal';
import { toast } from 'src/utils/appAlert';
import { ProfileEditContext } from '../ProfileEditContext';
import './IntroductionVideoSection.scss';
import { ReactComponent as ProfileRemove2Icon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/profile-remove2.svg';
import { ReactComponent as ProfileRemoveIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/profile-remove.svg';
import { ReactComponent as UploadIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/upload.svg';

import { ReactComponent as UploadFailedIcon } from '../../../../../../../assets/images/onboarding-trainer/profile/upload-failed.svg';
import { ReactComponent as FileIcon } from '../../../../../../../assets/images/onboarding-trainer/profile/file.svg';
import { ReactComponent as SizeIcon } from '../../../../../../../assets/images/onboarding-trainer/profile/size.svg';
import { ReactComponent as TimeIcon } from '../../../../../../../assets/images/onboarding-trainer/profile/time.svg';
import { ReactComponent as PlayButtonIcon } from '../../../../../../../assets/images/onboarding-trainer/profile/play-button.svg';
import { ReactComponent as DeleteIcon } from '../../../../../../../assets/images/onboarding-trainer/profile/delete.svg';
import { ReactComponent as ReplaceIcon } from '../../../../../../../assets/images/onboarding-trainer/profile/replace.svg';

const formatSize = (bytes?: number) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

const formatDuration = (seconds?: number) => {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

const formatFileName = (name?: string, maxLength: number = 14) => {
  if (!name) return '';
  if (name.length <= maxLength) return name;
  const extIndex = name.lastIndexOf('.');
  if (extIndex === -1) return name.substring(0, maxLength - 3) + '...';
  const ext = name.substring(extIndex);
  const baseName = name.substring(0, extIndex);
  const charsToKeep = maxLength - ext.length - 3;
  if (charsToKeep <= 0) return name.substring(0, Math.max(3, maxLength - 3)) + '...';
  const front = Math.ceil(charsToKeep / 2);
  const back = Math.floor(charsToKeep / 2);
  return `${baseName.substring(0, front)}...${baseName.substring(baseName.length - back)}${ext}`;
};

export const IntroductionVideoSection: FC = () => {
  const { currentUser } = sessionStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // Mock progress for UI

  const { mutateAsync: updateProfileBackend, isPending } = useUpdateTrainerProfile();

  const { globalEditAction, registerEditState } = useContext(ProfileEditContext);

  useEffect(() => {
    if (globalEditAction.count > 0) {
      if (globalEditAction.type === 'OPEN') {
        setIsEditing(true);
      } else if (globalEditAction.type === 'CLOSE') {
        setIsEditing(false);
      }
    }
  }, [globalEditAction]);

  useEffect(() => {
    registerEditState('introductionVideo', isEditing);
  }, [isEditing, registerEditState]);

  const [video, setVideo] = useState<{
    url?: string;
    name?: string;
    size?: number;
    duration?: number;
    error?: string;
  }>({
    url: currentUser?.introductionVideoUrl,
    name: currentUser?.introductionVideoName,
    size: currentUser?.introductionVideoSize,
    duration: currentUser?.introductionVideoDuration,
  });

  const [previewState, setPreviewState] = useState<string | null>(null);
  const [videoToRemove, setVideoToRemove] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (currentUser && !isEditing) {
      setVideo({
        url: currentUser.introductionVideoUrl,
        name: currentUser.introductionVideoName,
        size: currentUser.introductionVideoSize,
        duration: currentUser.introductionVideoDuration,
      });
    }
  }, [currentUser, isEditing]);

  const handleSave = async () => {
    if (!currentUser) return;

    if (!video.url && !currentUser.introductionVideoUrl) {
      return;
    }

    if (video.error) {
      toast.error('Please resolve validation errors before saving.');
      return;
    }

    try {
      const uid = currentUser?.uid || '';
      await updateProfileBackend({
        uid,
        data: {
          introductionVideoUrl: video.url || '',
          introductionVideoName: video.name || '',
          introductionVideoSize: video.size || null,
          introductionVideoDuration: video.duration || null,
        },
        oldVideoUrl:
          currentUser.introductionVideoUrl !== video.url
            ? currentUser.introductionVideoUrl
            : undefined,
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to save profile');
    }
  };

  const handleRemoveVideo = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setVideoToRemove(true);
  };

  const confirmRemoveVideo = () => {
    setVideo({
      url: undefined,
      name: undefined,
      size: undefined,
      duration: undefined,
      error: undefined,
    });
    setVideoToRemove(false);
  };

  const triggerReplace = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm'];

    if (!allowedTypes.includes(file.type)) {
      setVideo({
        ...video,
        error: 'Upload failed. Unsupported file format.',
      });
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      setVideo({
        ...video,
        error: 'Upload failed. File exceeds the 100 MB limit.',
      });
      return;
    }

    // Create a video element to check duration
    const videoElement = document.createElement('video');
    videoElement.preload = 'metadata';

    videoElement.onloadedmetadata = async () => {
      window.URL.revokeObjectURL(videoElement.src);
      if (videoElement.duration > 180) {
        // 3 minutes
        setVideo({
          ...video,
          error: 'Upload failed. Video exceeds 3 minutes limit.',
        });
        return;
      }

      // Proceed to upload
      setVideo({ ...video, name: file.name, error: undefined });

      try {
        setIsUploading(true);
        setUploadProgress(0);

        // Mock progress updates for UI while actual upload happens
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev >= 95) {
              clearInterval(progressInterval);
              return 95;
            }
            return prev + 5;
          });
        }, 100);

        const uid = currentUser?.uid || '';
        const downloadUrl = await uploadTrainerFile(file, 'videos', uid);

        clearInterval(progressInterval);
        setUploadProgress(100);

        setVideo({
          url: downloadUrl,
          name: file.name,
          size: file.size,
          duration: videoElement.duration,
          error: undefined,
        });
      } catch {
        toast.error('Failed to process file');
        setVideo({
          ...video,
          error: 'Upload failed. Please try again.',
        });
      } finally {
        setIsUploading(false);
      }
    };

    videoElement.onerror = () => {
      setVideo({
        ...video,
        error: 'Upload failed. Invalid video file.',
      });
    };

    videoElement.src = window.URL.createObjectURL(file);
  };

  const viewContent = (
    <div className="introduction-video-view">
      {currentUser?.introductionVideoUrl ? (
        <div className="uploaded-video-preview">
          <div className="preview-box">
            {currentUser.introductionVideoUrl && (
              <video
                src={currentUser.introductionVideoUrl}
                className="thumbnail-video"
                preload="metadata"
              />
            )}
            <button
              className="play-btn"
              onClick={() => setPreviewState(currentUser.introductionVideoUrl!)}
            >
              <PlayButtonIcon />
            </button>
          </div>
        </div>
      ) : (
        <div className="text-section-view">
          <div className="icon-wrapper">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="2" y="4" width="20" height="16" rx="2" stroke="white" strokeWidth="2" />
              <path d="M10 8L16 12L10 16V8Z" fill="white" />
            </svg>
          </div>
          <p>No video added yet.</p>
        </div>
      )}
    </div>
  );

  const renderUploadArea = () => {
    if (isUploading) {
      return (
        <div className="uploaded-video-preview">
          <div className="preview-box">
            <button className="play-btn" disabled>
              <PlayButtonIcon />
            </button>
            <div className="video-info-overlay">
              <span className="name">{formatFileName(video.name || 'Uploading...')}</span>
            </div>
          </div>
        </div>
      );
    }

    if (video.name && !video.error) {
      return (
        <div className="uploaded-video-preview">
          <div className="preview-box">
            {video.url && <video src={video.url} className="thumbnail-video" preload="metadata" />}
            <button className="play-btn" onClick={() => video.url && setPreviewState(video.url)}>
              <PlayButtonIcon />
            </button>
            <div className="video-info-overlay">
              <span className="name">{formatFileName(video.name)}</span>
              {video.size && video.duration && (
                <span className="meta">
                  {formatSize(video.size)} • {formatDuration(video.duration)}
                </span>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              accept="video/mp4,video/quicktime,video/webm"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={`upload-area ${video.error ? 'error-border' : ''}`}>
        <UploadIcon />
        <span>Upload video</span>
        <input
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          onChange={handleFileUpload}
        />
      </div>
    );
  };

  const editContent = (
    <div className="introduction-video-edit">
      {renderUploadArea()}

      <div className="upload-info-container">
        {isUploading && (
          <div className="uploading-progress-container">
            <div className="uploading-text">Uploading... {uploadProgress}%</div>
            <div className="progress-bar-bg">
              <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {video.name && !video.error && !isUploading && (
          <div className="actions-row">
            <button onClick={handleRemoveVideo}>
              <DeleteIcon />
            </button>
            <button onClick={triggerReplace}>
              <ReplaceIcon />
            </button>
          </div>
        )}

        {video.error && (
          <div className="error-message">
            <UploadFailedIcon /> <span>{video.error}</span>
          </div>
        )}

        <div className="upload-rules">
          <div className="rule-item">
            <FileIcon /> <span>MP4, MOV, or WebM</span>
          </div>
          <div className="rule-item">
            <SizeIcon /> <span>Max 100 MB</span>
          </div>
          <div className="rule-item">
            <TimeIcon /> <span>Up to 3 minutes</span>
          </div>
        </div>
      </div>
    </div>
  );

  const isInitialEmpty = !currentUser?.introductionVideoUrl;
  const isActionDisabled = isUploading || !!video.error || (!video.url && isInitialEmpty);

  return (
    <>
      <ProfileSection
        className="align-items-center introduction-video-header"
        title="Introduction Video"
        isEditing={isEditing}
        isSaving={isPending}
        isActionDisabled={isActionDisabled}
        onEditToggle={setIsEditing}
        onSave={handleSave}
        onCancel={() => {
          if (currentUser) {
            setVideo({
              url: currentUser.introductionVideoUrl,
              name: currentUser.introductionVideoName,
              size: currentUser.introductionVideoSize,
              duration: currentUser.introductionVideoDuration,
            });
          }
        }}
        editContent={editContent}
      >
        {viewContent}
      </ProfileSection>

      <FullScreenVideoModal
        isOpen={Boolean(previewState)}
        onClose={() => setPreviewState(null)}
        videoUrl={previewState || ''}
      />

      <Modal
        open={videoToRemove}
        footer={null}
        onCancel={() => setVideoToRemove(false)}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">
            <ProfileRemove2Icon />
          </div>
          <h2>Delete Video?</h2>
          <p>Are you sure you want to delete your introduction video?</p>
          <div className="logout-actions">
            <button className="btn-logout" onClick={confirmRemoveVideo}>
              <ProfileRemoveIcon /> Delete
            </button>
            <button className="btn-cancel" onClick={() => setVideoToRemove(false)}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
